import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';

import Main from '../../../layouts/Main';
import Container from '../../../components/Container';
import { Divider, Box, Paper, List, ListItem, ListItemText, AccordionDetails, Accordion, AccordionSummary } from '@mui/material';

import agencyAction from '../../../types/legal/agencyAction';
import ChronologicalButtonList from './ChronologicalButtonList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LegalTable from './LegalTable';
import PageBreadCrumbs from '../../../components/PageBreadcrumbs';

const AgencyActions = (): JSX.Element => {

  document.title = 'OCPF - Agency Actions';

  const [agencyActions, setAgencyActions] = useState<agencyAction[]>([]);

  useEffect(() => {
    getAgencyActions();
  }, [])


  const getAgencyActions = async () => {
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/legal/actions`)
        .then(result => result.json())
        .then(result => {
          setAgencyActions(result)
        });
    } catch (error) {
      console.error('Uncaught Error:', error);
    }

  }

  const publicResolutionLetters = agencyActions.filter((action) => action.actionTypeId === 1);
  const dispAgreements = agencyActions.filter((action) => action.actionTypeId === 2);
  const nonFilerRef = agencyActions.filter((action) => action.actionTypeId === 3);
  const agoActionsRefs = agencyActions.filter((action) => action.actionTypeId === 4);

  const years = Array.from({ length: new Date().getFullYear() - 2009 + 1 }, (_, i) => new Date().getFullYear() - i);

  const agencyActionDefinitions = [
    {
      title: 'Public Resolution Letters',
      description: 'A public resolution letter may be issued in instances where the office found "no reason to believe" a violation occurred; where "no further action" or investigation is warranted; or where a subject "did not comply" with the law but, in OCPF\'s view, the case is able to be settled in an informal fashion with an educational letter or a requirement that some corrective action be taken. A public resolution letter does not necessarily imply wrongdoing on the part of a subject.'
    },
    {
      title: 'Disposition Agreements',
      description: 'A disposition agreement is a voluntary written agreement entered into between the subject of a review and OCPF, in which the subject agrees to take certain specific actions. Disposition agreements are available below, under the public resolution letters.'
    },
    {
      title: 'Referral',
      description: 'OCPF has the option of referring matters to the Office of the Attorney General for further action. Referral may be made for failing to file required reports w/OCPF, or for other alleged violations of the Campaign Finance Law or OCPF regulations.'
    }
  ];

  return (
    <Main>
      <Container>
        <PageBreadCrumbs></PageBreadCrumbs>
        <Typography
          variant="h5"
          align={'left'}
          data-aos={'fade-up'}
          gutterBottom
          sx={{
            fontWeight: 700,
          }}
          paddingTop={3}>
          Agency Actions
        </Typography>
        <Divider />
        <Grid container marginTop={3}>
          <Grid size={8}>
            <Typography variant="body1" component={'p'} gutterBottom>
              OCPF audits all campaign finance reports and reviews complaints alleging violations of the campaign finance law. These audits and reviews may result in enforcement actions or rulings such as:
            </Typography>
            <Box>
              <List>
                {agencyActionDefinitions.map((action, index) => (

                  <ListItem key={index}>
                    <ListItemText
                      primary={action.title}
                      secondary={action.description}
                    />
                  </ListItem>

                ))}

              </List>
            </Box>
          </Grid>
          <Grid size={4}>
            <Box component={Paper} p={2}>
              <Typography variant="subtitle1" fontWeight={'bold'} gutterBottom>
                Confidentiality
              </Typography>
              <Typography variant="body1" component={'p'} gutterBottom>
                OCPF does not comment on any matter under review, nor does the office confirm or deny that it has received a specific complaint.
              </Typography>
              <Typography variant="body1" component={'p'} gutterBottom>
                The identity of any complainant is kept confidential. Public resolution letters and disposition agreements are matters of public record once cases are concluded.
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ ":hover": { backgroundColor: '#f5f5f5' } }}>
            Public Resolution Letters
          </AccordionSummary>
          <AccordionDetails>
            <ChronologicalButtonList years={years} agencyActions={publicResolutionLetters} eLibraryDocumentType={'Public Resolution Letters'} />

          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ ":hover": { backgroundColor: '#f5f5f5' } }}>
            Disposition Agreements
          </AccordionSummary>
          <AccordionDetails>
            <ChronologicalButtonList years={years} agencyActions={dispAgreements} eLibraryDocumentType={'Disposition Agreements'} />
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ ":hover": { backgroundColor: '#f5f5f5' } }}>
            Non-Filer Referrals to the Attorney General
          </AccordionSummary>
          <AccordionDetails>
            <LegalTable agencyActions={nonFilerRef} />
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ ":hover": { backgroundColor: '#f5f5f5' } }}>
            AGO Actions on OCPF Referrals
          </AccordionSummary>
          <AccordionDetails>
            <LegalTable agencyActions={agoActionsRefs} />
          </AccordionDetails>
        </Accordion>

      </Container>
    </Main>
  );
};

export default AgencyActions;